import { useId } from 'react';
import {
  Button,
  Checkbox,
  Input,
  InputDecoration,
  InputGroup,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  DropdownMenuLabel,
} from '@monksflow/monks-ui';
import { ChevronDown, ChevronRight, ListChecks, Search, X } from 'lucide-react';

import { getGuidelinesLimitMessage } from './ChatGuidelinesSelector.constants';
import type { ChatGuidelinesSelectorProps } from './ChatGuidelinesSelector.types';
import { useChatGuidelinesSelector } from './useChatGuidelinesSelector';
import { useTranslation } from 'react-i18next';

type Props = ChatGuidelinesSelectorProps & {
  disabled?: boolean;
};

export function ChatGuidelinesSelectorV2({ value, onChange, disabled }: Props) {
  const { t } = useTranslation('chats');

  const {
    isAiBusy,
    selectedCount,
    isAtLimit,
    search,
    filteredFolderData,
    isFolderExpanded,
    toggleFolder,
    toggleGuideline,
    toggleFolderGuidelines,
    getFolderSelectionState,
    selectAll,
    clearAll,
    setSearch,
    handleClose,
  } = useChatGuidelinesSelector({ value, onChange });

  const triggerDisabled = disabled || isAiBusy;
  const tooltipLabel =
    selectedCount > 0
      ? t('chats.chatGuidelinesSelectorV2.guidelinesWithCount', { selectedCount })
      : t('chats.chatGuidelinesSelectorV2.addGuidelines');

  const hasSelection = selectedCount > 0;
  const selectionId = useId();

  const handleClearAll = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    clearAll(e);
  };

  return (
    <DropdownMenu
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="group relative inline-flex items-center">
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className={
                  hasSelection ? 'h-8 rounded-lg px-2 gap-1 group-hover:pr-7' : 'h-8 w-8 rounded-lg'
                }
                aria-label={t('chats.chatGuidelinesSelectorV2.addGuidelines')}
                aria-describedby={hasSelection ? selectionId : undefined}
                disabled={triggerDisabled}
              >
                <span className="toolbar-icon-hover">
                  <ListChecks className="h-4 w-4" />
                </span>
                {hasSelection && (
                  <span className="text-xs font-medium text-foreground">{selectedCount}</span>
                )}
              </Button>
            </DropdownMenuTrigger>
            {hasSelection && (
              <span id={selectionId} hidden>
                {tooltipLabel}
              </span>
            )}
            {hasSelection && !triggerDisabled && (
              <button
                type="button"
                onClick={handleClearAll}
                aria-label={t('chats.chatGuidelinesSelectorV2.clearAllGuidelines')}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center justify-center size-4 rounded-sm text-foreground-subtle opacity-0 group-hover:opacity-100 transition-opacity hover:bg-muted hover:text-foreground"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p>{tooltipLabel}</p>
        </TooltipContent>
      </Tooltip>

      <DropdownMenuContent align="start" sideOffset={4} className="p-0 w-80">
        <div className="flex items-center justify-between gap-2 px-3 pt-3 pb-2">
          <DropdownMenuLabel className="py-2 px-0">
            {t('chats.chatGuidelinesSelectorV2.guidelines')}
          </DropdownMenuLabel>
          <div className="flex items-center gap-1">
            {hasSelection && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={(e: React.MouseEvent) => clearAll(e)}
              >
                {t('chats.chatGuidelinesSelectorV2.clear')}
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={(e: React.MouseEvent) => selectAll(e)}
            >
              {t('chats.chatGuidelinesSelectorV2.selectAll')}
            </Button>
          </div>
        </div>

        <div className="px-3 pb-2">
          <InputGroup
            size="sm"
            className="w-full border-0 has-[input:focus-visible]:ring-0 has-[input:focus-visible]:ring-offset-0"
          >
            <InputDecoration muted>
              <Search className="size-3.5" />
            </InputDecoration>
            <Input
              size="sm"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
              placeholder={t('chats.chatGuidelinesSelectorV2.search')}
            />
          </InputGroup>
        </div>

        {isAtLimit && (
          <div className="mx-3 mb-2 rounded-md border border-orange-200 bg-orange-50 px-2 py-1.5 text-xs text-orange-700">
            {getGuidelinesLimitMessage()}
          </div>
        )}

        <div className="max-h-72 overflow-y-auto px-1 pb-2">
          {filteredFolderData.length === 0 ? (
            <div className="px-3 py-4 text-center text-xs text-foreground-subtle">
              {t('chats.chatGuidelinesSelectorV2.noResults')}
            </div>
          ) : (
            filteredFolderData.map(({ folder, guidelines }) => {
              const state = getFolderSelectionState(folder.id);
              const expanded = isFolderExpanded(folder.id);
              const folderCheckedValue: boolean | 'indeterminate' =
                state === 'all' ? true : state === 'some' ? 'indeterminate' : false;
              // Only block when clicking would attempt to add ('none' at limit).
              // 'all' and 'some' are click-to-clear, so they must stay enabled
              // — otherwise users at the limit have no way to drop a folder.
              const folderCheckboxDisabled = state === 'none' && isAtLimit;

              return (
                <div key={folder.id}>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => toggleFolder(folder.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleFolder(folder.id);
                      }
                    }}
                    className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground cursor-pointer"
                  >
                    <Checkbox
                      checked={folderCheckedValue}
                      disabled={folderCheckboxDisabled}
                      onClick={(e) => e.stopPropagation()}
                      onCheckedChange={() => toggleFolderGuidelines(folder.id)}
                      className="data-[state=checked]:bg-foreground data-[state=checked]:border-foreground data-[state=indeterminate]:bg-foreground data-[state=indeterminate]:border-foreground"
                    />
                    <span className="flex-1 truncate" title={folder.title}>
                      {folder.title}
                    </span>
                    {expanded ? (
                      <ChevronDown className="h-4 w-4 text-foreground-subtle" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-foreground-subtle" />
                    )}
                  </div>

                  {expanded &&
                    (guidelines.length === 0 ? (
                      <div className="pl-7 py-1 text-xs text-foreground-subtle">
                        {t('chats.chatGuidelinesSelectorV2.noGuidelines')}
                      </div>
                    ) : (
                      guidelines.map((guideline) => {
                        const isSelected = value.includes(guideline.id);
                        const guidelineDisabled = !isSelected && isAtLimit;
                        return (
                          <DropdownMenuItem
                            key={guideline.id}
                            onClick={() => toggleGuideline(guideline.id)}
                            onKeyDown={(e: React.KeyboardEvent) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                toggleGuideline(guideline.id);
                              }
                            }}
                            className="pl-7"
                          >
                            <div className="flex items-center gap-2 w-full">
                              <Checkbox
                                checked={isSelected}
                                disabled={guidelineDisabled}
                                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                onCheckedChange={() => toggleGuideline(guideline.id)}
                                className="data-[state=checked]:bg-foreground data-[state=checked]:border-foreground"
                              />
                              <span className="flex-1 truncate" title={guideline.name}>
                                {guideline.name}
                              </span>
                            </div>
                          </DropdownMenuItem>
                        );
                      })
                    ))}
                </div>
              );
            })
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
